import { useEffect, useMemo, useRef, useState } from "react";
import { C, font } from "../styles/tokens";
import { useDense } from "./FormDensity";
import { InlineIcon } from "./InlineIcon";

/**
 * MonthYearPicker -- fully custom month + year picker.
 *
 * Replaces both the native <input type="month"> (OS-controlled UI) and any
 * native <select> approach (still OS-controlled). The trigger button opens
 * a popup with year navigation chevrons and a 4x3 grid of month buttons,
 * all styled with the app's design tokens so it looks the same on every
 * platform -- Windows, macOS, Android, iOS.
 *
 * Value contract: same as the previous version. Accepts and emits a
 * "yyyy-mm" string, or "" when unset. Empty value when month is missing.
 *
 * Props:
 *   label       string  -- caption above the trigger
 *   value       string  -- "yyyy-mm" or ""
 *   onChange    fn      -- called with the new "yyyy-mm" string
 *   required    bool    -- adds the red * to the label
 *   disabled    bool
 *   yearsBack   number  -- default 50
 *   yearsForward number -- default 5
 *   hint        string  -- small caption below the trigger
 */
const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function MonthYearPicker({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  yearsBack = 50,
  yearsForward = 5,
  hint,
  placeholder = "Select month and year",
  align = "left",
  containerStyle,
}) {
  const [open, setOpen] = useState(false);
  const dense = useDense();
  const triggerRef = useRef(null);
  const popupRef = useRef(null);

  const parsed = useMemo(() => {
    if (!value || typeof value !== "string") return { year: null, month: null };
    const [y, m] = value.split("-");
    const yi = parseInt(y, 10);
    const mi = parseInt(m, 10);
    return {
      year: Number.isFinite(yi) ? yi : null,
      month: Number.isFinite(mi) ? mi : null,
    };
  }, [value]);

  // The year shown in the popup panel. Independent of the selected year so
  // a user can browse "2024" without losing the "2026" they already chose
  // until they actually pick a new month.
  const [displayYear, setDisplayYear] = useState(
    parsed.year ?? new Date().getFullYear(),
  );

  // Reset the display year to the selected year (or this year) whenever the
  // popup opens. Otherwise re-opening shows whatever year they last browsed.
  useEffect(() => {
    if (open) {
      setDisplayYear(parsed.year ?? new Date().getFullYear());
    }
  }, [open, parsed.year]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    function onPointer(e) {
      if (popupRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const minYear = new Date().getFullYear() - yearsBack;
  const maxYear = new Date().getFullYear() + yearsForward;

  function selectMonth(monthNumber) {
    if (disabled) return;
    const mm = String(monthNumber).padStart(2, "0");
    onChange(`${displayYear}-${mm}`);
    setOpen(false);
  }

  function nudgeYear(delta) {
    setDisplayYear((y) => {
      const next = y + delta;
      if (next < minYear) return minYear;
      if (next > maxYear) return maxYear;
      return next;
    });
  }

  const display =
    parsed.year && parsed.month
      ? `${MONTHS_FULL[parsed.month - 1]} ${parsed.year}`
      : "";

  return (
    <div
      style={{
        marginBottom: dense ? 6 : 0,
        position: "relative",
        zIndex: open ? 60 : 1,
        ...containerStyle,
      }}
    >
      {label && (
        <label style={{ ...labelStyle, marginBottom: dense ? 2 : 6 }}>
          {label}
          {required && (
            <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>
          )}
        </label>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={triggerStyle(open, disabled, !display, dense)}
      >
        <span
          style={{
            flex: 1,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: display ? (disabled ? "#94a3b8" : "#1e293b") : "#94a3b8",
          }}
        >
          {display || placeholder}
        </span>
        <CalendarIcon disabled={disabled} />
      </button>

      {open && (
        <div ref={popupRef} role="dialog" style={getPopupStyle(align)}>
          {/* Year navigation */}
          <div style={yearNavStyle}>
            <button
              type="button"
              onClick={() => nudgeYear(-1)}
              disabled={displayYear <= minYear}
              aria-label="Previous year"
              style={yearNavBtnStyle(displayYear <= minYear)}
            >
              <InlineIcon name="chevronLeft" size={18} />
            </button>
            <div style={yearLabelStyle}>{displayYear}</div>
            <button
              type="button"
              onClick={() => nudgeYear(1)}
              disabled={displayYear >= maxYear}
              aria-label="Next year"
              style={yearNavBtnStyle(displayYear >= maxYear)}
            >
              <InlineIcon name="chevronRight" size={18} />
            </button>
          </div>

          {/* Month grid 4 cols x 3 rows */}
          <div style={monthGridStyle}>
            {MONTHS_SHORT.map((m, idx) => {
              const monthNumber = idx + 1;
              const isSelected =
                parsed.year === displayYear && parsed.month === monthNumber;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => selectMonth(monthNumber)}
                  style={monthBtnStyle(isSelected)}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {hint && (
        <div
          style={{ fontSize: 11, color: "#64748b", marginTop: dense ? 2 : 4 }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ----- styles -----

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 6,
  letterSpacing: 0.1,
};

function triggerStyle(open, disabled, isPlaceholder, dense) {
  return {
    width: "100%",
    height: dense ? 30 : 44,
    minHeight: dense ? 30 : 44,
    padding: dense ? "0 10px" : "0 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontFamily: font,
    fontWeight: 400,
    color: isPlaceholder ? "#94a3b8" : "#1e293b",
    background: disabled ? "#f1f5f9" : "#ffffff",
    border: `1px solid ${open ? "#2563eb" : "#cbd5e1"}`,
    borderRadius: 8,
    boxShadow: open ? "0 0 0 2px rgba(37,99,235,0.2)" : "none",
    cursor: disabled ? "not-allowed" : "pointer",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    opacity: disabled ? 0.7 : 1,
  };
}

function getPopupStyle(align) {
  return {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: align === "right" ? "auto" : 0,
    right: align === "right" ? 0 : "auto",
    zIndex: 70,
    // Just wide enough for the month grid + padding; never exceeds the viewport.
    width: "min(280px, calc(100vw - 32px))",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    boxShadow:
      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    padding: 12,
    fontFamily: font,
  };
}

const yearNavStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
  padding: "0 4px",
};

function yearNavBtnStyle(disabled) {
  return {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    lineHeight: 1,
    color: disabled ? C.textLight : C.textDark,
    background: "transparent",
    border: 0,
    borderRadius: 6,
    cursor: disabled ? "default" : "pointer",
    fontFamily: font,
    fontWeight: 700,
    opacity: disabled ? 0.4 : 1,
  };
}

const yearLabelStyle = {
  fontSize: 14,
  fontWeight: 700,
  color: C.textDark,
  letterSpacing: 0.2,
  fontVariantNumeric: "tabular-nums",
};

const monthGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 6,
};

function monthBtnStyle(isSelected) {
  return {
    height: 36,
    fontSize: 12,
    fontWeight: 600,
    color: isSelected ? "white" : "#1e293b",
    background: isSelected ? "#2563eb" : "transparent",
    border: `1px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: font,
    letterSpacing: 0.3,
    transition:
      "background 0.12s ease, color 0.12s ease, border-color 0.12s ease",
  };
}

function CalendarIcon({ disabled }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={disabled ? C.textLight : C.textMed}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
